import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Camera, CameraOff, Keyboard, CheckCircle2, XCircle, RotateCcw, Ticket, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PublicScanner() {
  const { scanKey } = useParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('camera');
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const scannerInstanceRef = useRef(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/tickets/public/scan-info/${scanKey}`);
        setEventInfo(res.data);
      } catch {
        setError('Invalid or expired scan link');
      }
    };
    if (scanKey) fetchInfo();
  }, [scanKey]);

  const startCamera = useCallback(async () => {
    if (scannerInstanceRef.current) return;
    try {
      const scanner = new Html5Qrcode('public-qr-reader');
      scannerInstanceRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => { handleScan(decodedText); },
        () => {}
      );
    } catch {
      toast.error('Camera access denied or not available');
      setScanning(false);
      scannerInstanceRef.current = null;
    }
  }, [scanKey]);

  const stopCamera = useCallback(async () => {
    if (scannerInstanceRef.current) {
      try {
        await scannerInstanceRef.current.stop();
        scannerInstanceRef.current.clear();
      } catch { /* cleanup */ }
      scannerInstanceRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      if (scannerInstanceRef.current) {
        try { scannerInstanceRef.current.stop(); scannerInstanceRef.current.clear(); } catch { /* */ }
      }
    };
  }, []);

  const handleScan = async (qrString) => {
    if (loading) return;
    await stopCamera();
    setLoading(true);
    setScanResult(null);

    try {
      const res = await axios.post(`${API_URL}/api/tickets/public/validate-qr/${scanKey}`, { qr_string: qrString });
      const data = res.data;
      const result = {
        valid: data.valid,
        message: data.message,
        reason: data.reason || '',
        attendee: data.attendee,
        ticket: data.ticket,
        event_title: data.event_title || '',
        qr: qrString,
        time: new Date().toLocaleTimeString(),
      };
      setScanResult(result);
      setScanHistory(prev => [result, ...prev].slice(0, 50));
      if (data.valid) toast.success(data.message || 'Ticket validated!');
      else toast.error(data.message || 'Invalid ticket');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to validate ticket';
      const result = {
        valid: false,
        message: msg,
        reason: msg,
        attendee: { name: 'Unknown' },
        ticket: { ticket_id: 'Not Found', ticket_type_name: 'Unknown' },
        event_title: eventInfo?.title || '',
        qr: qrString,
        time: new Date().toLocaleTimeString(),
      };
      setScanResult(result);
      setScanHistory(prev => [result, ...prev].slice(0, 50));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    handleScan(manualCode.trim());
    setManualCode('');
  };

  const handleScanAnother = () => {
    setScanResult(null);
    if (mode === 'camera') startCamera();
  };

  const checkedIn = scanHistory.filter(s => s.valid).length;
  const rejected = scanHistory.filter(s => !s.valid).length;

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-bold mb-2">Invalid Scan Link</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!eventInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-scanner-page">
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-primary" />
            <Badge variant="outline">NextGig Scanner</Badge>
          </div>
          <h1 className="text-xl font-bold" data-testid="public-scanner-event-title">{eventInfo.title}</h1>
          <p className="text-sm text-muted-foreground">{eventInfo.venue}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Card>
            <CardContent className="py-3 px-3 text-center">
              <p className="text-2xl font-bold">{eventInfo.stats?.total_tickets || 0}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 px-3 text-center">
              <p className="text-2xl font-bold text-emerald-500" data-testid="public-checked-in">{checkedIn + (eventInfo.stats?.checked_in || 0)}</p>
              <p className="text-xs text-muted-foreground">Checked In</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 px-3 text-center">
              <p className="text-2xl font-bold text-rose-500" data-testid="public-rejected">{rejected}</p>
              <p className="text-xs text-muted-foreground">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <Button variant={mode === 'camera' ? 'default' : 'outline'} className={mode === 'camera' ? 'flex-1 btn-primary' : 'flex-1'} onClick={() => { setMode('camera'); setScanResult(null); }} data-testid="public-mode-camera">
            <Camera className="w-4 h-4 mr-2" /> Camera
          </Button>
          <Button variant={mode === 'manual' ? 'default' : 'outline'} className={mode === 'manual' ? 'flex-1 btn-primary' : 'flex-1'} onClick={() => { stopCamera(); setMode('manual'); setScanResult(null); }} data-testid="public-mode-manual">
            <Keyboard className="w-4 h-4 mr-2" /> Manual
          </Button>
        </div>

        {/* Scanner / Result */}
        {scanResult ? (
          <Card className={`border-2 ${scanResult.valid ? 'border-emerald-500' : 'border-rose-500'}`} data-testid="public-scan-result">
            <CardContent className="py-8">
              <div className="flex flex-col items-center text-center">
                {scanResult.valid ? (
                  <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-rose-500/10 flex items-center justify-center mb-4">
                    <XCircle className="w-10 h-10 text-rose-500" />
                  </div>
                )}
                <h2 className={`text-xl font-bold mb-1 ${scanResult.valid ? 'text-emerald-500' : 'text-rose-500'}`} data-testid="public-scan-status">
                  {scanResult.valid ? 'VALID' : 'INVALID'}
                </h2>
                <p className="text-muted-foreground mb-4">{scanResult.message}</p>

                {/* Detailed scan info */}
                <div className="w-full bg-muted/50 rounded-lg p-4 mb-4 text-left space-y-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Ticket className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Scan Details</span>
                  </div>
                  <p className="text-sm" data-testid="public-scan-ticket-id">
                    <span className="text-muted-foreground">Ticket ID:</span>{' '}
                    <span className="font-mono">{scanResult.ticket?.ticket_id || 'Not Found'}</span>
                  </p>
                  <p className="text-sm" data-testid="public-scan-name">
                    <span className="text-muted-foreground">Name:</span>{' '}
                    {scanResult.attendee?.name || 'Unknown'}
                  </p>
                  <p className="text-sm" data-testid="public-scan-event">
                    <span className="text-muted-foreground">Event:</span>{' '}
                    {scanResult.event_title || eventInfo?.title || 'Unknown'}
                  </p>
                  {scanResult.ticket?.ticket_type_name && scanResult.ticket.ticket_type_name !== 'Unknown' && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Type:</span> {scanResult.ticket.ticket_type_name}
                    </p>
                  )}
                  {!scanResult.valid && scanResult.reason && (
                    <p className="text-sm text-rose-400 mt-1" data-testid="public-scan-reason">
                      <span className="text-muted-foreground">Reason:</span> {scanResult.reason}
                    </p>
                  )}
                </div>

                <Button onClick={handleScanAnother} className="btn-primary w-full" data-testid="public-scan-another">
                  <RotateCcw className="w-4 h-4 mr-2" /> Scan Another
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : mode === 'camera' ? (
          <Card>
            <CardContent className="py-4">
              <div id="public-qr-reader" className="w-full rounded-lg overflow-hidden mb-4" style={{ minHeight: scanning ? 300 : 0 }} />
              {!scanning ? (
                <Button onClick={startCamera} className="btn-primary w-full" disabled={loading} data-testid="public-start-camera">
                  <Camera className="w-4 h-4 mr-2" /> Start Camera
                </Button>
              ) : (
                <Button variant="outline" onClick={stopCamera} className="w-full" data-testid="public-stop-camera">
                  <CameraOff className="w-4 h-4 mr-2" /> Stop Camera
                </Button>
              )}
              {loading && (
                <div className="flex items-center justify-center mt-4 gap-2">
                  <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
                  <span className="text-sm text-muted-foreground">Validating...</span>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-6">
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Ticket ID or QR Code</label>
                  <Input value={manualCode} onChange={e => setManualCode(e.target.value)} placeholder="Enter ticket ID..." className="font-mono" data-testid="public-manual-input" />
                </div>
                <Button type="submit" className="btn-primary w-full" disabled={!manualCode.trim() || loading} data-testid="public-manual-validate">
                  {loading ? 'Validating...' : 'Validate Ticket'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scan History */}
        {scanHistory.length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Scan History ({scanHistory.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="public-scan-history">
                {scanHistory.map((entry, idx) => (
                  <div
                    key={`${entry.ticket?.ticket_id || ''}-${idx}`}
                    className={`flex items-start justify-between py-2.5 px-3 rounded-lg border ${
                      entry.valid ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'
                    }`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      {entry.valid ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold ${entry.valid ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {entry.valid ? 'VALID' : 'INVALID'}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground truncate">
                            {entry.ticket?.ticket_id || 'Not Found'}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{entry.attendee?.name || 'Unknown'}</p>
                        {!entry.valid && entry.reason && (
                          <p className="text-xs text-rose-400 truncate">{entry.reason}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">{entry.time}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
